import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { authService, companyService } from "./services/api";
import {
  getAuthItem,
  getAuthJson,
  setAuthItem,
} from "./utils/authStorage";
import EssProxyPage from "./pages/EssProxyPage";

// Admin Components
import Home from "./components/Admin/Home";
import Navbar from "./components/Admin/Navbar";
import Sidebar from "./components/Admin/Sidebar";
import LoginPage from "./components/auth/LoginPage";

// Eager imports: React.lazy + dynamic import() was resolving to undefined default
// exports at runtime (code-split chunk issue). Static imports guarantee a component.
import Attendance from "./components/Admin/Attendance";
import Employee from "./components/Admin/Employee";
import Payroll from "./components/Admin/Payroll";
import Reports from "./components/Admin/Reports";
import GlobalSettingsModal from "./components/Admin/GlobalSettingsModal";

// Employee Components
import EmployeeAttendance from "./Employee/EmployeeAttendance";
import EmployeeHome from "./Employee/EmployeeHome";
import EmployeePayroll from "./Employee/EmployeePayroll";
import EmployeeProfile from "./Employee/EmployeeProfile";
import EmployeeReports from "./Employee/EmployeeReports";
import MyTeam from "./Employee/MyTeam";
import "./App.css";
// Removed EmployeeNavbar - using single Navbar for both admin and employee
import Toast from "./Toast";
import Masters from "./components/Admin/masters/Masters";
import Configuration from "./components/Admin/attendance/components/Configuration";
import AuthHandler from "./components/auth/AuthHandler"; // New component for handling authentication
import HelpdeskConfigModal from "./components/Admin/HelpdeskConfigModal";
import Hiring from "./components/Admin/Hiring";
import Assets from "./components/Admin/Assets";
import { PermissionsProvider, clearRbacSnapshot } from "./rbac/PermissionsContext";
import RouteGate from "./rbac/RouteGate";

function ProtectedRoute({ children, allowedUserType }) {
  const navigate = useNavigate();
  const isAuthenticated = getAuthItem("isAuthenticated") === "true";
  const userType = getAuthItem("userType");
  const allowedTypes = useMemo(
    () =>
      Array.isArray(allowedUserType)
        ? allowedUserType
        : [allowedUserType],
    [allowedUserType]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    } else if (!allowedTypes.includes(userType)) {
      navigate("/home");
    }
  }, [isAuthenticated, navigate, allowedTypes, userType]);

  return isAuthenticated && allowedTypes.includes(userType) ? children : null;
}

function URLParameterHandler({ onLogin }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const companyId = urlParams.get("companyId");
    const superAdminID = urlParams.get("superAdminID");
    const password = urlParams.get("password");

    console.log(companyId, superAdminID, password);

    if (companyId && superAdminID && password) {
      // Auto-login with URL parameters
      onLogin("admin", companyId, superAdminID);

      // Clear URL parameters after login
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);

      // Navigate to unified home route
      navigate("/home");
    }
  }, [location, onLogin, navigate]);

  return null;
}

const COMPANY_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// Refresh company data once after login / reload (not on every route change).
function CompanyDataRefresher() {
  const isAuthenticated = getAuthItem("isAuthenticated") === "true";

  useEffect(() => {
    const refreshCompanyData = async () => {
      if (!isAuthenticated) return;

      try {
        const currentUser = getAuthJson("currentUser");
        if (currentUser?.companyId) {
          const cachedCompany = getAuthItem("companyData");
          const cachedTs = Number(getAuthItem("companyDataRefreshedAt") || 0);
          let cachedCompanyId = null;
          try {
            cachedCompanyId = cachedCompany ? JSON.parse(cachedCompany)?.id : null;
          } catch {
            cachedCompanyId = null;
          }
          const isFresh =
            !!cachedCompany &&
            String(cachedCompanyId || "") === String(currentUser.companyId) &&
            Number.isFinite(cachedTs) &&
            Date.now() - cachedTs < COMPANY_REFRESH_INTERVAL_MS;
          if (isFresh) return;

          await companyService.refreshCompanyData(currentUser.companyId);
          setAuthItem("companyDataRefreshedAt", String(Date.now()));
        }
      } catch (error) {
        console.warn("⚠️ Company data refresh skipped:", error);
        // Don't throw error - refresh is optional
      }
    };

    // Refresh only once per authenticated session (with staleness check)
    refreshCompanyData();
  }, [isAuthenticated]);

  return null;
}

function App() {
  const [, setCurrentPage] = useState("Home");
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return getAuthItem("isAuthenticated") === "true";
  });
  const [userType, setUserType] = useState(() => {
    return getAuthItem("userType") || "";
  });
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [companyName, setCompanyName] = useState(() => {
    const company = getAuthJson("companyData");
    return company?.name || "";
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  // Update company name when company data changes
  useEffect(() => {
    const company = getAuthJson("companyData");
    if (company) {
      setCompanyName(company.name);
      // Update document title
      if (company && company.name) {
        document.title = company.name + " - PaceHRM";
      } else {
        document.title = "PaceHRM";
      }
    } else {
      document.title = "PaceHRM";
    }
  }, []);

  // Listen for localStorage changes to update title when company data changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "companyData") {
        try {
          if (e.newValue) {
            const company = JSON.parse(e.newValue);
            if (company && company.name) {
              document.title = company.name + " - PaceHRM";
              setCompanyName(company.name);
            } else {
              document.title = "PaceHRM";
              setCompanyName("");
            }
          } else {
            document.title = "PaceHRM";
            setCompanyName("");
          }
        } catch (error) {
          console.error("Error parsing company data:", error);
          document.title = "PaceHRM";
          setCompanyName("");
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((prevMode) => {
      const newMode = !prevMode;
      localStorage.setItem("darkMode", String(newMode));
      return newMode;
    });
  }, []);

  const handleLogin = useCallback((type, companyId, superAdminID) => {
    setIsAuthenticated(true);
    setUserType(type);
    setAuthItem("isAuthenticated", "true");
    setAuthItem("userType", type);
    if (companyId !== undefined && companyId !== null) {
      setAuthItem("companyId", String(companyId));
    }
    if (superAdminID !== undefined && superAdminID !== null) {
      setAuthItem("superAdminID", String(superAdminID));
    }

    // Update company name after login
    const company = getAuthJson("companyData");
    if (company?.name) {
      setCompanyName(company.name);
    }
  }, []);

  const handleLogout = useCallback(() => {
    authService.logout();
    clearRbacSnapshot();
    setIsAuthenticated(false);
    setUserType("");
    setCompanyName("");
    window.location.href = "/login"; // Force redirect to login
  }, []);

  const renderAdminContent = (pageName) => {
    const props = {
      darkMode,
      setCurrentPage,
      toggleDarkMode,
    };

    switch (pageName) {
      case "Home":
        return <Home {...props} />;
      case "Masters":
        return <Masters {...props} />;
      case "Time & Attendance":
        return <Attendance {...props} />;
      case "Employee":
        return <Employee {...props} />;
      case "Payroll":
        return <Payroll {...props} />;
      case "Hiring":
        return <Hiring />;
      case "Assets":
        return <Assets />;
      case "Helpdesk":
        return <HelpdeskConfigModal variant="page" />;
      case "Reports":
        return <Reports {...props} />;
      case "Global Settings":
        return <GlobalSettingsModal initialTab="policy" />;
      case "Communication":
        return <GlobalSettingsModal initialTab="communication" />;
      case "User Role Management":
        return <GlobalSettingsModal initialTab="user_role_management" />;
      case "Configuration":
        return <Configuration darkMode={darkMode} />;
      default:
        return <Home {...props} />;
    }
  };

  const renderAdminLayout = (pageName) => (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <Sidebar
        setCurrentPage={setCurrentPage}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
      />
      {renderAdminContent(pageName)}
    </div>
  );

  // Renders employee page components (Profile/Attendance/Payroll/Reports)
  // inside the admin shell so only the middle content swaps while the top nav
  // and both side panels remain intact. Mirrors renderAdminLayout so the
  // surrounding chrome is identical between admin and employee pages.
  const renderAdminShellWithEmployee = (employeeElement) => (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <Sidebar
        setCurrentPage={setCurrentPage}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
      />
      {employeeElement}
    </div>
  );

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <Router>
        <Toast />
        <div
          className={`flex h-screen overflow-hidden font-sans antialiased ${darkMode ? "dark" : ""}`}
        >
          <div className="flex w-full items-center justify-center bg-redhalo-gray/30 dark:bg-[#121212]">
            <div className="text-center">
              <div
                className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-gray-200 border-t-redhalo-red dark:border-[#3C3C4C] dark:border-t-redhalo-mint"
                aria-hidden
              />
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Processing authentication…
              </p>
            </div>
          </div>
        </div>
      </Router>
    );
  }

  return (
    <Router>
      <Toast />
      <PermissionsProvider>
      <AuthHandler
        setIsAuthenticated={setIsAuthenticated}
        setUserType={setUserType}
        setIsCheckingAuth={setIsCheckingAuth}
      />
      <URLParameterHandler onLogin={handleLogin} />
      <CompanyDataRefresher />
      <div
        className={`flex h-screen overflow-hidden font-sans antialiased ${darkMode ? "dark" : ""}`}
      >
        <div className="flex flex-1 flex-col overflow-hidden">
          {isAuthenticated && (
            <>
              <Navbar
                darkMode={darkMode}
                handleLogout={handleLogout}
                isEmployeeView={userType === "employee"}
              />
            </>
          )}
          <main
            className="scrollbar-hide flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden bg-redhalo-gray/30 font-sans antialiased text-gray-900 transition-[padding] duration-300 dark:bg-[#121212] dark:text-gray-100"
            style={
              isAuthenticated
                ? {
                    paddingLeft: "var(--admin-left-sidebar-width, 0px)",
                    paddingRight: "var(--admin-right-sidebar-width, 0px)",
                  }
                : undefined
            }
          >
            <Routes>
              {/* Login Route */}
              <Route
                path="/login"
                element={
                  isAuthenticated ? (
                    <Navigate to="/home" replace />
                  ) : (
                    <LoginPage onLogin={handleLogin} darkMode={darkMode} />
                  )
                }
              />

              {/* Unified Home Route - all roles use admin shell */}
              <Route
                path="/home"
                element={
                  isAuthenticated ? (
                    renderAdminLayout("Home")
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute allowedUserType={["admin", "employee"]}>
                    <Routes>
                      <Route
                        path="home"
                        element={
                          <RouteGate module="home">
                            {renderAdminLayout("Home")}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="configuration"
                        element={
                          <RouteGate module="configuration">
                            {renderAdminLayout("Configuration")}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="masters"
                        element={
                          <RouteGate module="configuration">
                            {renderAdminLayout("Masters")}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="attendance"
                        element={
                          <RouteGate module="attendance">
                            {renderAdminLayout("Time & Attendance")}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="employee"
                        element={
                          <RouteGate module="employee">
                            {renderAdminLayout("Employee")}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="payroll"
                        element={
                          <RouteGate module="payroll">
                            {renderAdminLayout("Payroll")}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="hiring"
                        element={
                          <RouteGate module="hiring">
                            {renderAdminLayout("Hiring")}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="assets"
                        element={
                          <RouteGate module="assets">
                            {renderAdminLayout("Assets")}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="communication"
                        element={
                          <RouteGate module="communication">
                            {renderAdminLayout("Communication")}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="helpdesk"
                        element={
                          <RouteGate module="helpdesk">
                            {renderAdminLayout("Helpdesk")}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="user-role-management"
                        element={
                          <RouteGate module="user_roles">
                            {renderAdminLayout("User Role Management")}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="reports"
                        element={renderAdminLayout("Reports")}
                      />
                      <Route
                        path="global-settings"
                        element={renderAdminLayout("Global Settings")}
                      />
                    </Routes>
                  </ProtectedRoute>
                }
              />

              {/* Employee Routes - render inside admin shell so only the
                  middle content swaps while admin navbar + side panels stay. */}
              <Route
                path="/employee/*"
                element={
                  <ProtectedRoute allowedUserType={["employee", "admin"]}>
                    <Routes>
                      <Route
                        path="home"
                        element={
                          <RouteGate
                            module="home"
                            screen="dashboard"
                            action="view"
                            redirectTo="/employee/profile"
                          >
                            {renderAdminShellWithEmployee(<EmployeeHome />)}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="profile"
                        element={
                          <RouteGate
                            module="profile"
                            screen="org_details"
                            action="view"
                            redirectTo="/employee/home"
                          >
                            {renderAdminShellWithEmployee(<EmployeeProfile />)}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="attendance"
                        element={
                          <RouteGate
                            module="ess_attendance"
                            screen="self_service"
                            action="view"
                            redirectTo="/employee/home"
                          >
                            {renderAdminShellWithEmployee(<EmployeeAttendance />)}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="payroll"
                        element={
                          <RouteGate
                            module="ess_payroll"
                            screen="self_service"
                            action="view"
                            redirectTo="/employee/home"
                          >
                            {renderAdminShellWithEmployee(<EmployeePayroll />)}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="reports"
                        element={
                          <RouteGate
                            module="ess_reports"
                            screen="reports"
                            action="view"
                            redirectTo="/employee/home"
                          >
                            {renderAdminShellWithEmployee(<EmployeeReports />)}
                          </RouteGate>
                        }
                      />
                      <Route
                        path="my-team"
                        element={
                          <RouteGate
                            module="my_team"
                            screen="reportee_list"
                            action="view"
                            redirectTo="/employee/home"
                          >
                            {renderAdminShellWithEmployee(<MyTeam />)}
                          </RouteGate>
                        }
                      />
                    </Routes>
                  </ProtectedRoute>
                }
              />

              {/* ESS Proxy redemption page — public, no auth required */}
              <Route path="/ess-proxy" element={<EssProxyPage />} />

              {/* Default Route */}
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    <Navigate to="/home" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
            </Routes>
          </main>
        </div>
      </div>
      </PermissionsProvider>
    </Router>
  );
}

export default App;
